import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { workIdService, WorkIdRecord, WorkIdQueryParams, WorkIdFilterOptions, WorkIdListFilters } from '../workIdService';
import WorkIdActionsMenu from './WorkIdActionsMenu';
import { useWorkIdActionItems } from '../hooks/useWorkIdActionItems';
import { usePermissions } from '../../../hooks/usePermissions';
import { Alert, Spinner, Button, Table, OverlayTrigger, Tooltip } from '@/components/ui';
import { groupLegendByServiceArea } from '../utils/mapLegend';
import { hasRenderableSize } from '../utils/mapFit';
import { formatDateTime } from '@/utils/displayDateTime';
import { describeActionError } from '../../../utils/loadError';
import { pointsToFit } from '../utils/mapFit';
// One shared libraries list: the loader is a page-wide singleton (see hooks/useGoogleMaps).
import { GOOGLE_MAPS_LIBRARIES } from '../../../hooks/useGoogleMaps';

/** Stable color palette for foreman areas (matches reference design). */
const FOREMAN_AREA_COLORS = [
  '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231', '#911eb4',
  '#42d4f4', '#f032e6', '#bfef45', '#fabed4', '#469990', '#dcbeff',
  '#9a6324', '#fffac8', '#800000', '#aaffc3', '#808000', '#ffd8b1',
  '#000075', '#808080'
];

// British Columbia, framed to the province rather than all of Canada; the map then fits the data.
const DEFAULT_CENTER = { lat: 53.7, lng: -124.5 };
const DEFAULT_ZOOM = 6;
const MAX_FIT_ZOOM = 12;

interface WorkIdsMapViewProps {
  refreshKey: number;
  filters: WorkIdListFilters;
  search?: string;
  filterOptions: WorkIdFilterOptions;
  onApprove?: (record: WorkIdRecord) => void;
  onCancel?: (record: WorkIdRecord) => void;
  onCloseWithoutAction?: (record: WorkIdRecord) => void;
  onReopen?: (record: WorkIdRecord) => void;
  onAssignExisting?: (record: WorkIdRecord) => void;
  onCreateNewTask?: (record: WorkIdRecord) => void;
  onUnassign?: (record: WorkIdRecord) => void;
  onMerge?: (record: WorkIdRecord) => void;
  onViewMergeSuggestions?: (record: WorkIdRecord) => void;
  onUnmerge?: (record: WorkIdRecord) => void;
  onReviewHistory?: (record: WorkIdRecord) => void;
  onEdit?: (record: WorkIdRecord) => void;
  onReopenTask?: (record: WorkIdRecord) => void;
  onReportAccomplishment?: (record: WorkIdRecord) => void;
  onCloseTask?: (record: WorkIdRecord) => void;
}

const formatReportedBy = (record: WorkIdRecord): string => {
  if (!record.reportedBy) return '-';
  const { name, lastName } = record.reportedBy;
  return [name, lastName].filter(Boolean).join(' ') || '-';
};

const formatDate = (dateStr: string): string => formatDateTime(dateStr) || dateStr;

const formatAsset = (record: WorkIdRecord): string => {
  if (!record.asset) return '-';
  const name = record.asset.name || '';
  const num = record.asset.assetNumber;
  return num ? `${name} #${num}` : name || '-';
};

const POPUP_W = 320;
const POPUP_MAX_H = 400;
const POPUP_PAD = 12;

/**
 * Use OverlayView projection to get accurate marker position in container pixels.
 */
function useMarkerPixelPosition(
  map: google.maps.Map | null,
  lat: number | undefined,
  lng: number | undefined,
  boundsVersion: number
): { markerPx: number; markerPy: number } | null {
  const [pos, setPos] = useState<{ markerPx: number; markerPy: number } | null>(null);

  useEffect(() => {
    if (!map || lat == null || lng == null) {
      setPos(null);
      return;
    }
    const latLng = new google.maps.LatLng(lat, lng);
    const overlay = new (class extends google.maps.OverlayView {
      onAdd() {}
      draw() {
        const proj = this.getProjection();
        if (!proj) return;
        const pixel = proj.fromLatLngToContainerPixel(latLng);
        if (pixel) setPos({ markerPx: pixel.x, markerPy: pixel.y });
      }
      onRemove() {}
    })();
    overlay.setMap(map);
    return () => {
      overlay.setMap(null);
      setPos(null);
    };
  }, [map, lat, lng, boundsVersion]);

  return pos;
}

interface MapMarkerProps {
  record: WorkIdRecord & { latitude: number; longitude: number };
  color: string;
  onHover: () => void;
  onHoverEnd: () => void;
  onClick: (e: google.maps.MapMouseEvent) => void;
}

const MapMarker: React.FC<MapMarkerProps> = ({ record, color, onHover, onHoverEnd, onClick }) => {
  return (
    <Marker
      position={{ lat: record.latitude, lng: record.longitude }}
      icon={{
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: color,
        fillOpacity: 1,
        strokeColor: '#333',
        strokeWeight: 1
      }}
      onMouseOver={onHover}
      onMouseOut={onHoverEnd}
      onClick={onClick}
    />
  );
};

const WorkIdsMapView: React.FC<WorkIdsMapViewProps> = ({
  refreshKey,
  filters,
  search,
  filterOptions,
  onApprove,
  onCancel,
  onCloseWithoutAction,
  onReopen,
  onAssignExisting,
  onCreateNewTask,
  onUnassign,
  onMerge,
  onViewMergeSuggestions,
  onUnmerge,
  onReviewHistory,
  onEdit,
  onReopenTask,
  onReportAccomplishment,
  onCloseTask
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  const [workIds, setWorkIds] = useState<WorkIdRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [mapBoundsVersion, setMapBoundsVersion] = useState(0);
  const hoverShowTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverEndTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; record: WorkIdRecord } | null>(null);
  const [exportingId, setExportingId] = useState<number | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  const { hasPermission } = usePermissions();
  const hasWorkIdPerm = (key: string) => hasPermission(`workId:${key}`);
  const canExport = hasWorkIdPerm('view');

  const actionCallbacks = useMemo(
    () => ({
      onEdit,
      onApprove,
      onCancel,
      onCloseWithoutAction,
      onReopen,
      onReopenTask,
      onReportAccomplishment,
      onCloseTask,
      onAssignExisting,
      onCreateNewTask,
      onUnassign,
      onMerge,
      onViewMergeSuggestions,
      onUnmerge,
      onReviewHistory,
      onExportToPdf: canExport
        ? async (record: WorkIdRecord) => {
            setExportError(null);
            try {
              setExportingId(record.id);
              const blob = await workIdService.exportToPdf(record.id);
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              const label = (record.workIdentificationId || `WorkID_${record.id}`).replace(/[/\\?*:<>|"\s]/g, '_');
              a.download = `WorkID_${label}_${new Date().toISOString().split('T')[0]}.pdf`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            } catch (err) {
              console.error('Export to PDF failed:', err);
              const label = record.workIdentificationId ? ` for ${record.workIdentificationId}` : '';
              setExportError(describeActionError(err, `Couldn't export the PDF${label}. Try again.`));
            } finally {
              setExportingId(null);
            }
          }
        : undefined
    }),
    [
      onEdit,
      onApprove,
      onCancel,
      onCloseWithoutAction,
      onReopen,
      onReopenTask,
      onReportAccomplishment,
      onCloseTask,
      onAssignExisting,
      onCreateNewTask,
      onUnassign,
      onMerge,
      onViewMergeSuggestions,
      onUnmerge,
      onReviewHistory,
      canExport
    ]
  );

  const foremanAreaColorMap = useMemo(() => {
    const areas = [...(filterOptions.foremanAreas || [])].sort((a, b) => a.name.localeCompare(b.name));
    const map = new Map<number | string, string>();
    map.set(0, '#9ca3af'); // (Blank)
    areas.forEach((area, idx) => {
      map.set(area.id, FOREMAN_AREA_COLORS[idx % FOREMAN_AREA_COLORS.length]);
    });
    return map;
  }, [filterOptions.foremanAreas]);

  const getMarkerColor = useCallback(
    (record: WorkIdRecord): string => {
      const areaId = record.foremanAreaId ?? 0;
      const areaName = record.foremanArea?.name;
      if (areaName) {
        const area = filterOptions.foremanAreas?.find(a => a.name === areaName);
        if (area) return foremanAreaColorMap.get(area.id) ?? FOREMAN_AREA_COLORS[0];
      }
      return foremanAreaColorMap.get(areaId) ?? foremanAreaColorMap.get(0) ?? FOREMAN_AREA_COLORS[0];
    },
    [foremanAreaColorMap, filterOptions.foremanAreas]
  );

  useEffect(() => {
    const controller = new AbortController();
    let aborted = false;

    const fetchWorkIds = async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Omit<WorkIdQueryParams, 'page' | 'limit'> = {
          ...(filters.status && filters.status !== 'exclude-cancelled' && { status: filters.status }),
          ...(filters.foremanAreaId && { foremanAreaId: filters.foremanAreaId }),
          ...(filters.serviceAreaId && { serviceAreaId: filters.serviceAreaId }),
          ...(filters.assetType && { assetType: filters.assetType }),
          ...(search && search.trim() && { search: search.trim() }),
          ...(filters.priority && { priority: filters.priority }),
          ...(filters.reportedById && { reportedById: filters.reportedById }),
          ...(filters.workSource && { workSource: filters.workSource }),
          ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
          ...(filters.dateTo && { dateTo: filters.dateTo }),
          ...((filters.dateFilterType === 'taskCompletion' || filters.dateFilterType === 'reportedOn') && {
            dateFilterType: filters.dateFilterType
          })
        };
        const { data } = await workIdService.getWorkIdsForMap(params, controller.signal);
        if (!controller.signal.aborted) {
          setWorkIds(data.filter((w) => w.latitude != null && w.longitude != null));
        }
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError('Failed to load work IDs for map.');
          setWorkIds([]);
        }
      } finally {
        if (!aborted) setLoading(false);
      }
    };

    fetchWorkIds();
    return () => {
      aborted = true;
      controller.abort();
    };
  }, [
    refreshKey,
    filters.serviceAreaId,
    filters.foremanAreaId,
    filters.assetType,
    filters.status,
    filters.priority,
    filters.reportedById,
    filters.workSource,
    filters.dateFrom,
    filters.dateTo,
    filters.dateFilterType,
    search
  ]);

  useEffect(
    () => () => {
      if (hoverShowTimeoutRef.current) clearTimeout(hoverShowTimeoutRef.current);
      if (hoverEndTimeoutRef.current) clearTimeout(hoverEndTimeoutRef.current);
    },
    []
  );

  const markersWithCoords = useMemo(
    () => workIds.filter((w) => w.latitude != null && w.longitude != null) as (WorkIdRecord & { latitude: number; longitude: number })[],
    [workIds]
  );

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapInstance(map);
  }, []);

  const hoveredRecord = hoveredId ? markersWithCoords.find((w) => w.id === hoveredId) : null;
  const markerPos = useMarkerPixelPosition(
    mapInstance,
    hoveredRecord?.latitude,
    hoveredRecord?.longitude,
    mapBoundsVersion
  );

  // V2-069: frame the pins in the service region. One pin with a bad coordinate (0,0, a sign
  // flipped, a point in Ontario) used to stretch the view to Hudson Bay with BC in a corner.
  const bounds = useMemo(() => {
    const points = pointsToFit(markersWithCoords.map((w) => ({ lat: w.latitude!, lng: w.longitude! })));
    if (points.length === 0) return null;
    const b = new google.maps.LatLngBounds();
    points.forEach((p) => b.extend(p));
    return b;
  }, [markersWithCoords]);

  // Fit once both the map and the data are there, whichever arrives last (W-M15). This used to
  // depend on the data only, so data that landed before the map finished loading never fitted
  // and the map stayed at the all-of-Canada default with BC in a corner.
  //
  // V3-078: fitting also has to wait for the map to have its real size. A fit made while the
  // map's box is still collapsed (0 px, or a sliver while the page lays out) picks a continent-
  // wide zoom that then sticks once the box grows - Test opened on all of Canada. So the fit is
  // skipped until the box has a size, and redone whenever the box is resized, until the user
  // moves the map themselves.
  const userMovedMapRef = useRef(false);
  useEffect(() => {
    userMovedMapRef.current = false;
  }, [bounds]);
  useEffect(() => {
    if (!bounds || !mapInstance || markersWithCoords.length === 0) return;
    const div = mapInstance.getDiv();
    const fit = () => {
      if (userMovedMapRef.current || !hasRenderableSize(div?.clientWidth ?? 0, div?.clientHeight ?? 0)) return;
      mapInstance.fitBounds(bounds, 50);
      // A single pin (or a tight cluster) would otherwise zoom in to street level.
      google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
        if ((mapInstance.getZoom() ?? 0) > MAX_FIT_ZOOM) mapInstance.setZoom(MAX_FIT_ZOOM);
      });
    };
    fit();
    const drag = mapInstance.addListener('dragstart', () => {
      userMovedMapRef.current = true;
    });
    const observer = typeof ResizeObserver !== 'undefined' && div ? new ResizeObserver(() => fit()) : null;
    if (observer && div) observer.observe(div);
    return () => {
      observer?.disconnect();
      drag.remove();
    };
  }, [bounds, mapInstance, markersWithCoords.length]);

  const legendGroups = useMemo(() => {
    const byId = new Map((filterOptions.foremanAreas || []).map((fa) => [fa.id, fa]));
    const byName = new Map((filterOptions.foremanAreas || []).map((fa) => [fa.name, fa]));
    const seen = new Map<string | number, { name: string; color: string; serviceArea?: string | number | null }>();
    markersWithCoords.forEach((w) => {
      const key = w.foremanAreaId ?? 0;
      if (seen.has(key)) return;
      const label = w.foremanArea?.name || '(Blank)';
      const option = (w.foremanAreaId != null ? byId.get(w.foremanAreaId) : undefined) ?? byName.get(label);
      seen.set(key, {
        name: label,
        color: getMarkerColor(w),
        serviceArea: option ? option.serviceAreaName || option.serviceAreaId : null,
      });
    });
    return groupLegendByServiceArea(Array.from(seen.values()));
  }, [markersWithCoords, getMarkerColor, filterOptions.foremanAreas]);

  if (loadError) {
    return (
      <Alert variant="warning">
        Google Maps failed to load. Ensure REACT_APP_GOOGLE_MAPS_API_KEY is set.
      </Alert>
    );
  }

  if (!isLoaded) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-p-5">
        <Spinner animation="border" role="status">Loading map…</Spinner>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div className="work-ids-map-view tw-flex tw-flex-col tw-grow">
      <h4 className="tw-mb-2 tw-shrink-0">Map of Works Identified</h4>
      {exportError && (
        <Alert variant="danger" role="alert" dismissible onClose={() => setExportError(null)} className="tw-mb-2 tw-shrink-0">
          {exportError}
        </Alert>
      )}
      {/* Legend (W-M15): foreman areas grouped by service area, A–Z within each group, groups laid
          out two to a row. Labels truncate; the full name is in a tap-friendly tooltip. */}
      {legendGroups.length > 0 && (
        <div
          className="tw-mb-2 tw-grid tw-shrink-0 tw-grid-cols-1 tw-gap-x-6 tw-gap-y-2 sm:tw-grid-cols-2"
          aria-label="Foreman area colours"
          data-testid="work-id-map-legend"
        >
          {legendGroups.map((group) => (
            <div key={group.label} className="tw-min-w-0">
              <div className="tw-text-xs tw-font-semibold tw-tracking-wide tw-text-content-secondary" title={group.title}>
                {group.label}
              </div>
              <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-x-3 tw-gap-y-1 tw-p-0">
                {group.items.map((fa) => (
                  <li key={fa.name} className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1 tw-text-sm">
                    <span
                      className="tw-inline-block tw-shrink-0 tw-rounded-full"
                      style={{ width: 10, height: 10, backgroundColor: fa.color }}
                      aria-hidden
                    />
                    <OverlayTrigger placement="top" overlay={<Tooltip id={`legend-${group.label}-${fa.name}`}>{fa.name}</Tooltip>}>
                      <span className="tw-truncate" style={{ maxWidth: 120 }} tabIndex={0}>
                        {fa.name}
                      </span>
                    </OverlayTrigger>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      <div className="tw-grow" style={{ minHeight: 400, position: 'relative' }}>
        {loading && (
          // Cover the map while Work IDs load so it is never just a blank grey panel (W-M15).
          <div
            className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-surface/70"
            style={{ zIndex: 10 }}
            role="status"
            data-testid="work-id-map-loading"
          >
            <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-surface tw-px-4 tw-py-3 tw-shadow-card">
              <Spinner animation="border" size="sm" aria-hidden="true" />
              <span className="tw-text-sm tw-text-content-secondary">Loading work IDs…</span>
            </div>
          </div>
        )}
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          onLoad={onMapLoad}
          onIdle={() => setMapBoundsVersion((v) => v + 1)}
          options={{
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true
          }}
        >
          {markersWithCoords.map((record) => (
            <MapMarker
              key={record.id}
              record={record}
              color={getMarkerColor(record)}
              onHover={() => {
                if (hoverEndTimeoutRef.current) {
                  clearTimeout(hoverEndTimeoutRef.current);
                  hoverEndTimeoutRef.current = null;
                }
                if (hoverShowTimeoutRef.current) {
                  clearTimeout(hoverShowTimeoutRef.current);
                }
                hoverShowTimeoutRef.current = setTimeout(() => setHoveredId(record.id), 120);
              }}
              onHoverEnd={() => {
                if (hoverShowTimeoutRef.current) {
                  clearTimeout(hoverShowTimeoutRef.current);
                  hoverShowTimeoutRef.current = null;
                }
                // Keep info window visible while context menu is open
                if (!contextMenu) {
                  hoverEndTimeoutRef.current = setTimeout(() => setHoveredId(null), 400);
                }
              }}
              onClick={(e) => {
                if (hoverShowTimeoutRef.current) {
                  clearTimeout(hoverShowTimeoutRef.current);
                  hoverShowTimeoutRef.current = null;
                }
                if (hoverEndTimeoutRef.current) {
                  clearTimeout(hoverEndTimeoutRef.current);
                  hoverEndTimeoutRef.current = null;
                }
                // Keep info visible when opening actions menu
                setHoveredId(record.id);
                const dom = e.domEvent as MouseEvent | undefined;
                if (dom) {
                  setContextMenu({ x: dom.clientX, y: dom.clientY, record });
                }
              }}
            />
          ))}
        </GoogleMap>
        {hoveredId && hoveredRecord && (() => {
          const record = hoveredRecord;
          const map = mapRef.current;
          const mapDiv = map?.getDiv();
          if (!mapDiv || !markerPos) return null;
          const mapW = mapDiv.offsetWidth || 400;
          const mapH = mapDiv.offsetHeight || 500;
          const { markerPx, markerPy } = markerPos;

          // Place box to whichever side has more room, vertically centred on marker
          const b = map?.getBounds();
          const preferRight = !b || record.longitude < (b.getNorthEast().lng() + b.getSouthWest().lng()) / 2;
          const GAP = 14; // gap between marker edge and popup box
          let left = preferRight ? markerPx + GAP : markerPx - POPUP_W - GAP;
          left = Math.max(POPUP_PAD, Math.min(mapW - POPUP_W - POPUP_PAD, left));
          let top = markerPy - POPUP_MAX_H / 2;
          top = Math.max(POPUP_PAD, Math.min(mapH - POPUP_MAX_H - POPUP_PAD, top));

          const popup = (
            <div
              className="tw-absolute tw-bg-surface tw-rounded tw-shadow tw-border tw-border-line tw-text-sm"
              style={{ left, top, width: POPUP_W, maxHeight: POPUP_MAX_H, overflowY: 'auto', zIndex: 1000 }}
              onMouseEnter={() => {
                if (hoverEndTimeoutRef.current) {
                  clearTimeout(hoverEndTimeoutRef.current);
                  hoverEndTimeoutRef.current = null;
                }
              }}
              onMouseLeave={() => {
                if (!contextMenu) {
                  hoverEndTimeoutRef.current = setTimeout(() => setHoveredId(null), 400);
                }
              }}
            >
              <div className="tw-absolute tw-top-0 tw-right-0 tw-p-1">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="tw-text-secondary tw-p-0"
                  style={{ fontSize: '1.25rem', lineHeight: 1 }}
                  onClick={() => setHoveredId(null)}
                  aria-label="Close"
                >×</Button>
              </div>
              <div
                className="tw-bg-surface-alt tw-border-b tw-border-line tw-px-2 tw-py-1 tw-font-semibold"
                style={{ fontSize: '1rem', marginBottom: 8, lineHeight: 1.4 }}
              >
                Work ID: {record.workIdentificationId || record.id}
              </div>
              <Table size="sm" borderless className="tw-mb-0 tw-text-sm">
                <tbody>
                  <tr><td className="tw-text-content-muted tw-pr-2">Foreman Area</td><td>{record.foremanArea?.name || '-'}</td></tr>
                  <tr><td className="tw-text-content-muted tw-pr-2">Latitude</td><td>{record.latitude}</td></tr>
                  <tr><td className="tw-text-content-muted tw-pr-2">Longitude</td><td>{record.longitude}</td></tr>
                  <tr><td className="tw-text-content-muted tw-pr-2">Inspector Name</td><td>{formatReportedBy(record)}</td></tr>
                  <tr><td className="tw-text-content-muted tw-pr-2">Date Reported</td><td>{formatDate(record.reportedOn)}</td></tr>
                  <tr><td className="tw-text-content-muted tw-pr-2">Highway Unique</td><td>{record.asset?.highwayUnique || '-'}</td></tr>
                  <tr><td className="tw-text-content-muted tw-pr-2">Road Name</td><td>{formatAsset(record)}</td></tr>
                  <tr><td className="tw-text-content-muted tw-pr-2 tw-align-top">Comment</td><td style={{ wordBreak: 'break-word' }}>{record.comments || '-'}</td></tr>
                </tbody>
              </Table>
            </div>
          );
          return ReactDOM.createPortal(popup, mapDiv);
        })()}
      </div>
      <div className="tw-text-content-muted tw-text-sm tw-mt-2 tw-shrink-0">
        Showing {markersWithCoords.length} work ID{markersWithCoords.length !== 1 ? 's' : ''} with coordinates.
        {workIds.length > markersWithCoords.length && ` (${workIds.length - markersWithCoords.length} without coordinates hidden)`}
      </div>

      {contextMenu && (
        <MapMarkerContextMenu
          record={contextMenu.record}
          x={contextMenu.x}
          y={contextMenu.y}
          actionCallbacks={actionCallbacks}
          exportingId={exportingId}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};

interface MapMarkerContextMenuProps {
  record: WorkIdRecord;
  x: number;
  y: number;
  actionCallbacks: Parameters<typeof useWorkIdActionItems>[1];
  exportingId: number | null;
  onClose: () => void;
}

const MapMarkerContextMenu: React.FC<MapMarkerContextMenuProps> = ({
  record,
  x,
  y,
  actionCallbacks,
  exportingId,
  onClose
}) => {
  const items = useWorkIdActionItems(record, actionCallbacks, exportingId);
  if (items.length === 0) return null;
  return (
    <WorkIdActionsMenu left={x} top={y} onClose={onClose} items={items} />
  );
};

export default WorkIdsMapView;
